import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoaderCircle, Unlink } from 'lucide-react';
import { toast } from 'sonner';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';

export function SettingsSlack(): React.ReactElement {
  const { isAdmin, workspace } = useWorkspace();
  const orpc = useOrpc();
  const queryClient = useQueryClient();

  const statusQuery = useQuery({
    ...orpc.slack.getInstallStatus.queryOptions({ input: {} }),
    enabled: isAdmin,
  });

  const linksQuery = useQuery({
    ...orpc.slack.listUserLinks.queryOptions({ input: {} }),
    enabled: isAdmin && statusQuery.data?.installed === true,
  });

  const invalidateLinks = () => {
    void queryClient.invalidateQueries({
      queryKey: orpc.slack.listUserLinks.queryOptions({ input: {} }).queryKey,
    });
  };

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['slack'] });
  };

  const unlinkMutation = useMutation(
    orpc.slack.unlinkUser.mutationOptions({
      onSuccess: () => {
        invalidateLinks();
        toast.success('User unlinked');
      },
      onError: () => toast.error('Failed to unlink user'),
    }),
  );

  const uninstallMutation = useMutation(
    orpc.slack.uninstall.mutationOptions({
      onSuccess: () => {
        invalidateAll();
        toast.success('Slack app uninstalled');
      },
      onError: () => toast.error('Failed to uninstall'),
    }),
  );

  if (!isAdmin) {
    return <p className="text-sm text-muted-foreground">Only workspace admins can manage the Slack integration.</p>;
  }

  if (statusQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 py-8">
        <LoaderCircle size={16} className="animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading Slack status...</span>
      </div>
    );
  }

  if (statusQuery.isError) {
    return (
      <section>
        <h2 className="mb-4 text-sm font-bold tracking-wider text-muted-foreground uppercase">Slack</h2>
        <div className="rounded border border-destructive/30 bg-destructive/5 p-6 text-center">
          <p className="text-sm font-medium text-destructive">Failed to load Slack status</p>
          <p className="mt-2 text-xs text-muted-foreground">Please try refreshing the page.</p>
        </div>
      </section>
    );
  }

  const status = statusQuery.data;

  if (status && 'configured' in status && !status.configured) {
    return (
      <section>
        <h2 className="mb-4 text-sm font-bold tracking-wider text-muted-foreground uppercase">Slack</h2>
        <div className="rounded border border-amber-500/30 bg-amber-500/5 p-6 text-center">
          <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Slack integration is not configured</p>
          <p className="mt-2 text-xs text-muted-foreground">
            The server is missing required Slack environment variables (SLACK_CLIENT_ID, SLACK_CLIENT_SECRET,
            SLACK_SIGNING_SECRET, SLACK_APP_ID). Contact the administrator to configure the Slack app.
          </p>
        </div>
      </section>
    );
  }

  if (!status?.installed) {
    return (
      <section>
        <h2 className="mb-4 text-sm font-bold tracking-wider text-muted-foreground uppercase">Slack</h2>
        <div className="rounded border border-border bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground">Slack is not installed for this workspace.</p>
          <a
            href={`/slack/install?workspace_id=${encodeURIComponent(workspace.id)}`}
            className="mt-4 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Install Slack App
          </a>
        </div>
      </section>
    );
  }

  const links = linksQuery.data ?? [];

  return (
    <section>
      <h2 className="mb-4 text-sm font-bold tracking-wider text-muted-foreground uppercase">Slack</h2>

      {/* Install status */}
      <div className="mb-6 rounded border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Installed</p>
            <p className="text-xs text-muted-foreground">Team: {status.teamId}</p>
          </div>
          <button
            type="button"
            className="rounded-md border border-destructive/30 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10"
            disabled={uninstallMutation.isPending}
            onClick={() => {
              if (status.teamId) {
                uninstallMutation.mutate({ teamId: status.teamId });
              }
            }}
          >
            {uninstallMutation.isPending ? 'Uninstalling...' : 'Uninstall'}
          </button>
        </div>
      </div>

      {/* Linked users */}
      <h3 className="mb-2 text-xs font-bold tracking-wider text-muted-foreground uppercase">Linked Users</h3>
      {links.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No users have linked their Slack accounts yet. Users can link via <code>/lookout connect</code> in Slack.
        </p>
      ) : (
        <div className="rounded border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 text-left text-xs text-muted-foreground">
                <th className="px-4 py-2">Slack User</th>
                <th className="px-4 py-2">Webapp User</th>
                <th className="px-4 py-2">Linked</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {links.map((link) => (
                <tr key={`${link.teamId}-${link.slackUserId}`} className="border-b border-border/40 last:border-0">
                  <td className="px-4 py-2 font-mono text-xs">{link.slackUserId}</td>
                  <td className="px-4 py-2 font-mono text-xs">{link.userId}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{link.createdAt}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs text-destructive hover:text-destructive/80"
                      disabled={unlinkMutation.isPending}
                      onClick={() => unlinkMutation.mutate({ teamId: link.teamId, slackUserId: link.slackUserId })}
                    >
                      <Unlink size={12} />
                      Unlink
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Help footer */}
      <div className="mt-6 rounded border border-border/60 bg-muted/30 p-4">
        <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase">Slack Commands</h3>
        <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
          <li>
            <code>/lookout connect</code> — Link your Slack account to this workspace
          </li>
          <li>
            <code>/lookout help</code> — Show available commands
          </li>
        </ul>
      </div>
    </section>
  );
}
